import { useEffect, useState } from "react";
import api, { showApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { buildReportHtml, REPORT_SECTIONS } from "@/lib/report";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Flame, Target, Dumbbell, TrendingDown, Save, Lightbulb, FileDown, RefreshCw, Sparkles, Check, ArrowUpCircle, Crown, ListChecks, Stethoscope } from "lucide-react";
import { toast } from "sonner";

const GOALS = [
  ["perdita_peso", "Perdita di peso"], ["ricomposizione", "Ricomposizione corporea"],
  ["tonificazione", "Tonificazione"], ["mantenimento", "Mantenimento"], ["massa", "Aumento massa"],
];
const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-4))"];

export default function Overview() {
  const { user } = useAuth();
  const isPremium = !!user?.is_premium;
  const [data, setData] = useState(null);
  const [weights, setWeights] = useState([]);
  const [goal, setGoal] = useState("");
  const [saving, setSaving] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insBusy, setInsBusy] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [sections, setSections] = useState(() => Object.fromEntries(REPORT_SECTIONS.map((s) => [s.key, true])));
  const [plan, setPlan] = useState(null); // {week, habits, checked}

  const load = async () => {
    const [p, w] = await Promise.all([api.get("/profile"), api.get("/weight")]);
    setData(p.data);
    setGoal(p.data.profile?.goal || "");
    setWeights(w.data.logs || []);
  };
  useEffect(() => { load(); }, []);

  const genInsights = async () => {
    setInsBusy(true);
    try {
      const { data: res } = await api.post("/insights/generate");
      setInsights(res);
      try { const { data: ap } = await api.get("/action-plan"); setPlan(ap); } catch {}
    } catch (err) { showApiError(err); }
    finally { setInsBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await api.get("/insights");
        if (res && res.summary) setInsights(res);
        else if (isPremium) genInsights();
      } catch {}
      try { const { data: ap } = await api.get("/action-plan"); setPlan(ap); } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremium]);

  const toggleHabit = async (habit) => {
    setPlan((pl) => pl ? { ...pl, checked: pl.checked.includes(habit) ? pl.checked.filter((h) => h !== habit) : [...pl.checked, habit] } : pl);
    try { const { data } = await api.post("/action-plan/toggle", { habit }); setPlan((pl) => ({ ...pl, ...data })); }
    catch (err) {
      showApiError(err);
      try { const { data: ap } = await api.get("/action-plan"); setPlan(ap); } catch {}
    }
  };

  const runReport = async () => {
    setReportBusy(true);
    try {
      const { data: r } = await api.get("/report");
      const html = buildReportHtml(r, sections);
      const w = window.open("", "_blank");
      if (!w) { toast.error("Consenti i popup per scaricare il PDF"); return; }
      w.document.write(html); w.document.close(); w.focus();
      setTimeout(() => w.print(), 500);
      setReportOpen(false);
    } catch (err) { showApiError(err); }
    finally { setReportBusy(false); }
  };

  const [docBusy, setDocBusy] = useState(false);
  const doctorReport = async () => {
    setDocBusy(true);
    try {
      const { data: r } = await api.get("/report");
      const clinical = { profile: true, insights: false, diet: false, supplements: false, blood: true, weight: true };
      const html = buildReportHtml(r, clinical, {
        title: "Referto clinico per il medico",
        subtitle: "Sintesi clinica: dati essenziali, analisi del sangue con interpretazione e andamento del peso.",
      });
      const w = window.open("", "_blank");
      if (!w) { toast.error("Consenti i popup per scaricare il PDF"); return; }
      w.document.write(html); w.document.close(); w.focus();
      setTimeout(() => w.print(), 500);
    } catch (err) { showApiError(err); }
    finally { setDocBusy(false); }
  };

  const saveGoal = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/goal", { goal });
      setData((d) => ({ ...d, profile: res.profile, targets: res.targets }));
      toast.success("Obiettivo aggiornato. Rigenera la dieta per applicarlo.");
    } catch (err) {
      showApiError(err);
    } finally { setSaving(false); }
  };

  if (!data) return <div className="grid md:grid-cols-3 gap-4">{[0,1,2].map(i => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>;

  const t = data.targets || {};
  const profile = data.profile || {};
  const macroData = [
    { name: "Proteine", value: t.protein_g || 0 },
    { name: "Carboidrati", value: t.carbs_g || 0 },
    { name: "Grassi", value: t.fat_g || 0 },
  ];
  const start = weights[0]?.weight_kg;
  const current = weights[weights.length - 1]?.weight_kg ?? profile.weight_kg;
  const delta = start != null && current != null ? (current - start) : 0;

  return (
    <div className="space-y-6" data-testid="overview-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold">Ciao, {data.name?.split(" ")[0]} 👋</h2>
          <p className="text-muted-foreground">Ecco il tuo quadro nutrizionale personalizzato.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={doctorReport} disabled={docBusy} data-testid="doctor-report-button">
            <Stethoscope className="h-4 w-4 mr-2" /> {docBusy ? "Preparo…" : "Referto per il medico"}
          </Button>
          <Button variant="outline" onClick={() => setReportOpen(true)} data-testid="download-report-button">
            <FileDown className="h-4 w-4 mr-2" /> Scarica report completo
          </Button>
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileDown className="h-5 w-5" /> Report completo</DialogTitle>
            <DialogDescription>Scegli le sezioni da includere nel PDF.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5">
            {REPORT_SECTIONS.map((s) => (
              <label key={s.key} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/40"
                data-testid={`report-section-${s.key}`}>
                <Checkbox checked={sections[s.key]} onCheckedChange={(v) => setSections((sx) => ({ ...sx, [s.key]: !!v }))} />
                <span className="text-sm font-medium">{s.label}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} data-testid="report-cancel">Annulla</Button>
            <Button onClick={runReport} disabled={reportBusy || !Object.values(sections).some(Boolean)} data-testid="report-confirm">
              <FileDown className="h-4 w-4 mr-2" /> {reportBusy ? "Preparo…" : "Scarica PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Flame className="h-4 w-4 text-accent" /> Fabbisogno giornaliero</CardTitle></CardHeader>
          <CardContent>
            <div className="font-mono-data text-4xl font-semibold" data-testid="target-kcal">{t.target_kcal || "—"}</div>
            <p className="text-sm text-muted-foreground mt-1">kcal · TDEE {t.tdee || "—"}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Obiettivo</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-heading font-semibold">{t.goal_label || "—"}</div>
            <p className="text-sm text-muted-foreground mt-1">Peso obiettivo: <span className="font-mono-data">{profile.target_weight_kg} kg</span></p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><TrendingDown className="h-4 w-4 text-clinical" /> Variazione peso</CardTitle></CardHeader>
          <CardContent>
            <div className="font-mono-data text-4xl font-semibold">{current ?? "—"}<span className="text-lg text-muted-foreground"> kg</span></div>
            <p className={`text-sm mt-1 ${delta <= 0 ? "text-primary" : "text-accent"}`}>{delta === 0 ? "Nessuna variazione" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)} kg dall'inizio`}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-primary/25 bg-gradient-to-br from-primary/5 to-accent/5" data-testid="insights-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-accent" /> Considerazioni sul tuo stile di vita
            </CardTitle>
            {isPremium && (
              <Button variant="ghost" size="sm" onClick={genInsights} disabled={insBusy} data-testid="insights-refresh-button">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${insBusy ? "animate-spin" : ""}`} /> Aggiorna
              </Button>
            )}
          </div>
          <p className="text-sm text-muted-foreground">Una visione d'insieme che unisce anamnesi, analisi, abitudini, dieta e peso.</p>
        </CardHeader>
        <CardContent>
          {!isPremium ? (
            <div className="text-center py-6 space-y-3">
              <p className="text-muted-foreground text-sm max-w-md mx-auto">Sblocca le considerazioni di DietFlix su misura che collegano tutti i tuoi dati in consigli concreti.</p>
              <Button onClick={() => window.dispatchEvent(new CustomEvent("dietflix:premium-required"))} data-testid="insights-upgrade-button">
                <Crown className="h-4 w-4 mr-2" /> Passa a Premium e sblocca
              </Button>
            </div>
          ) : insBusy && !insights ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-4 rounded" />)}</div>
          ) : insights ? (
            <div className="space-y-4" data-testid="insights-content">
              {insights.summary && <p className="text-sm">{insights.summary}</p>}
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: "strengths", label: "Punti di forza", icon: Check, cls: "text-primary" },
                  { key: "improvements", label: "Aree da migliorare", icon: ArrowUpCircle, cls: "text-accent" },
                  { key: "habits", label: "Abitudini consigliate", icon: Sparkles, cls: "text-primary" },
                  { key: "focus_markers", label: "Da monitorare", icon: Target, cls: "text-clinical" },
                ].map(({ key, label, icon: Ic, cls }) => (
                  (insights[key] || []).length > 0 && (
                    <div key={key} data-testid={`insights-${key}`}>
                      <div className={`font-medium text-sm flex items-center gap-1.5 mb-1.5 ${cls}`}><Ic className="h-4 w-4" /> {label}</div>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
                        {insights[key].map((x, i) => <li key={i}>{x}</li>)}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <Button onClick={genInsights} disabled={insBusy} data-testid="insights-generate-button">
                <Sparkles className="h-4 w-4 mr-2" /> Genera considerazioni
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {plan && plan.habits && plan.habits.length > 0 && (
        <Card className="rounded-2xl" data-testid="action-plan-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-primary" /> Piano d'azione della settimana
              </CardTitle>
              {plan.streak > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent border border-accent/30 px-3 py-1 text-sm font-semibold" data-testid="habit-streak">
                  <Flame className="h-4 w-4" /> {plan.streak} {plan.streak === 1 ? "settimana" : "settimane"} di fila
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Spunta le abitudini che completi. Si azzera ogni lunedì · {plan.checked.length}/{plan.habits.length} completate
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {plan.habits.map((h, i) => {
              const done = plan.checked.includes(h);
              return (
                <label key={i} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${done ? "bg-primary/5 border-primary/30" : "hover:bg-muted/40"}`}
                  data-testid={`habit-${i}`}>
                  <Checkbox checked={done} onCheckedChange={() => toggleHabit(h)} className="mt-0.5" data-testid={`habit-check-${i}`} />
                  <span className={`text-sm ${done ? "line-through text-muted-foreground" : ""}`}>{h}</span>
                </label>
              );
            })}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-xl">Distribuzione macronutrienti</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={macroData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={3}>
                    {macroData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-around text-center mt-2">
              {macroData.map((m, i) => (
                <div key={m.name}>
                  <div className="font-mono-data font-semibold flex items-center gap-1.5 justify-center">
                    <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ backgroundColor: COLORS[i] }} />
                    {m.value}g
                  </div>
                  <div className="text-xs text-muted-foreground">{m.name}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader><CardTitle className="font-heading text-xl flex items-center gap-2"><Dumbbell className="h-5 w-5 text-primary" /> Aggiorna obiettivo</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">Cambia obiettivo in base ai tuoi progressi; ricalcola automaticamente calorie e macro.</p>
            <Select value={goal} onValueChange={setGoal}>
              <SelectTrigger data-testid="goal-select"><SelectValue placeholder="Seleziona obiettivo" /></SelectTrigger>
              <SelectContent>{GOALS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={saveGoal} disabled={saving} data-testid="save-goal-button">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Salvataggio…" : "Salva obiettivo"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
